import React, { useState } from "react";
import {
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Collapse,
  Box,
  IconButton,
  Tooltip,
  Typography,
  Menu as MuiMenu,
  MenuItem as MuiMenuItem,
} from "@mui/material";
import { NavLink, useLocation } from "react-router-dom";
import { ReactComponent as Logo } from "../images/ikdki.svg";
import { useUser } from "../pages/UserContext";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import MenuIcon from "@mui/icons-material/Menu";
import { resolveMenuIcon } from "../utils/menuIcons";

// Menu items are driven by the backend Role/Menu access-control system
// (see controller/AccessControl.js `buildMenuTree`) instead of a hardcoded
// list. The backend only knows menu icons by string name, so resolve them
// to components via the shared registry — also used by the menu-management
// icon picker, so an icon chosen there is guaranteed to render here too.
const resolveIcon = (iconName) => resolveMenuIcon(iconName);

const Sidebar = ({ onItemClick, collapsed = false, onToggleCollapse }) => {
  const userDetails = useUser();
  const menuItems = userDetails?.menus || [];
  const location = useLocation();
  const [open, setOpen] = useState({});
  const [flyout, setFlyout] = useState(null);

  const handleToggle = (text) => {
    setOpen((prevOpen) => ({ ...prevOpen, [text]: !prevOpen[text] }));
  };

  const handleOpenFlyout = (event, key) => {
    setFlyout({ key, anchorEl: event.currentTarget });
  };

  const handleCloseFlyout = () => setFlyout(null);

  const isActive = (itemPath, dynamicPath) => {
    const base = dynamicPath || itemPath;
    return (
      location.pathname === itemPath ||
      location.pathname.startsWith(`${base}/`)
    );
  };

  // Shared styling for every clickable row — kept as one place so the
  // collapsed (icon-only) and expanded (icon+label) variants stay visually
  // consistent instead of drifting apart.
  const itemSx = (active) => ({
    justifyContent: "center",
    backgroundColor: active ? "#003158" : "white",
    "&:hover": {
      backgroundColor: "#003158",
      "& .MuiListItemText-primary": { color: "#fff" },
      "& .MuiListItemIcon-root": { color: "#fff" },
    },
    "& .MuiListItemText-primary": {
      color: active ? "#fff" : "#003158",
      textAlign: "left",
    },
  });

  const iconSx = (active) => ({
    color: active ? "#fff" : "#003158",
    minWidth: collapsed ? 0 : 56,
    justifyContent: "center",
  });

  return (
    <div>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: collapsed ? "center" : "space-between",
          px: 2,
          py: 1.5,
          borderBottom: "1px solid #eee",
        }}
      >
        {!collapsed && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Logo style={{ width: 40, height: "auto" }} />
            <Typography sx={{ fontWeight: 700, color: "#003158", fontSize: "1.1rem" }}>
              IKDKI
            </Typography>
          </Box>
        )}
        <IconButton onClick={onToggleCollapse} size="small">
          <MenuIcon sx={{ color: "#003158" }} />
        </IconButton>
      </Box>

      <List sx={{ width: "100%", height: "100%", marginTop: "0" }}>
        {menuItems.map((item) => {
          const active = isActive(item.path, item.dynamicPath);

          if (item.children) {
            if (collapsed) {
              return (
                <div key={item.key}>
                  <Tooltip title={item.label} placement="right">
                    <ListItemButton
                      onClick={(e) => handleOpenFlyout(e, item.key)}
                      sx={itemSx(active)}
                    >
                      <ListItemIcon sx={iconSx(active)}>
                        {resolveIcon(item.icon)}
                      </ListItemIcon>
                    </ListItemButton>
                  </Tooltip>
                  <MuiMenu
                    anchorEl={flyout?.key === item.key ? flyout.anchorEl : null}
                    open={flyout?.key === item.key}
                    onClose={handleCloseFlyout}
                    anchorOrigin={{ vertical: "top", horizontal: "right" }}
                    transformOrigin={{ vertical: "top", horizontal: "left" }}
                  >
                    {item.children.map((child) => (
                      <MuiMenuItem
                        key={child.key}
                        component={NavLink}
                        to={child.path}
                        onClick={() => {
                          handleCloseFlyout();
                          onItemClick?.();
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 32, color: "#003158" }}>
                          {resolveIcon(child.icon)}
                        </ListItemIcon>
                        {child.label}
                      </MuiMenuItem>
                    ))}
                  </MuiMenu>
                </div>
              );
            }

            return (
              <div key={item.key}>
                <ListItemButton
                  onClick={() => handleToggle(item.key)}
                  sx={itemSx(active)}
                >
                  <ListItemIcon sx={iconSx(active)}>
                    {resolveIcon(item.icon)}
                  </ListItemIcon>
                  <ListItemText primary={item.label} />
                  {open[item.key] ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>
                <Collapse in={open[item.key]} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {item.children.map((child) => {
                      const childActive = isActive(
                        child.path,
                        child.dynamicPath
                      );
                      return (
                        <ListItemButton
                          key={child.key}
                          component={NavLink}
                          to={child.path}
                          onClick={onItemClick}
                          sx={{ pl: 4, ...itemSx(childActive) }}
                        >
                          <ListItemIcon sx={iconSx(childActive)}>
                            {resolveIcon(child.icon)}
                          </ListItemIcon>
                          <ListItemText primary={child.label} />
                        </ListItemButton>
                      );
                    })}
                  </List>
                </Collapse>
              </div>
            );
          }

          return (
            <Tooltip
              key={item.key}
              title={collapsed ? item.label : ""}
              placement="right"
            >
              <ListItemButton
                component={NavLink}
                to={item.path}
                onClick={onItemClick}
                sx={itemSx(active)}
              >
                {item.icon && (
                  <ListItemIcon sx={iconSx(active)}>
                    {resolveIcon(item.icon)}
                  </ListItemIcon>
                )}
                {!collapsed && (
                  <ListItemText primary={item.label} sx={{ textAlign: "left" }} />
                )}
              </ListItemButton>
            </Tooltip>
          );
        })}
      </List>
    </div>
  );
};

export default Sidebar;
